import {
  Box,
  Button,
  chakra,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { AdminClientListViewQuery } from '@/@generated/codegen/API';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useTableQueryParamsUtils } from '@/hooks/useTableQueryParamsUtils';
import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters, { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';

import useAdminClientTableStore from './AdminClientListTableStore';
import getColumns, { AdminClientListItemType } from './columns';
import CreateClientForm from '../CreateClientForm/CreateClientForm';

type Props = {
  data: AdminClientListViewQuery;
  pagination: ReturnType<typeof usePaginationState>;
  handleDeleteClient: (row: AdminClientListItemType) => void;
  tablePrefix: string;
};

const filterExtraInfo: FilterExtraInfo<AdminClientListItemType> = [
  {
    filterId: 'createdAt',
    filterType: FilterType.DATE,
  },
];

export default function AdminClientListView({
  data,
  pagination,
  handleDeleteClient,
  tablePrefix,
}: Props) {
  const { t } = useTranslation();
  const totalCount = data.listClients.totalCount;
  const { isOpen: isFormOpen, onOpen: openForm, onClose: closeForm } = useDisclosure();

  const columns = getColumns({
    t,
    onDeleteClientClick: handleDeleteClient,
  });
  const {
    columnFilters,
    sorting,
    globalFilter,
    onColumnFiltersChange,
    onSortingChange,
    onGlobalFilterChange,
  } = useTableQueryParamsUtils(filterExtraInfo, tablePrefix);
  const [columnVisibility, setColumnVisibility, columnSizing, setColumnSizing] =
    useAdminClientTableStore((state) => [
      state.columnVisibility,
      state.setColumnVisibility,
      state.columnSizing,
      state.setColumnSizing,
    ]);

  const table = useReactTable({
    data: data.listClients.edges.map((edge) => edge.node),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.paginationState.pageSize),
    state: {
      pagination: pagination.paginationState,
      columnSizing,
      columnVisibility,
      sorting,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    onPaginationChange: pagination.createOnPaginationChangeFunc(data.listClients.pageInfo),
    onGlobalFilterChange: onGlobalFilterChange,
    onSortingChange: onSortingChange,
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columns: columns,
  });

  return (
    <Box maxW="full" p={{ base: 3, md: 3 }}>
      <Flex justifyContent="space-between" wrap="wrap" gap={2} alignItems="center" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold">
          {t('admin.clients.clientsList.title')}
        </chakra.h3>
        <Popover placement="bottom-start" isOpen={isFormOpen} onClose={closeForm}>
          <PopoverTrigger>
            <Button
              background="blue.400"
              color="white"
              size={{ base: 'sm', md: 'md' }}
              onClick={openForm}
            >
              {t('admin.clients.button.createNew')}
            </Button>
          </PopoverTrigger>
          <PopoverContent w="auto" padding="10px" boxShadow="lg">
            <PopoverBody>
              <CreateClientForm closeForm={closeForm} />
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
      <>
        <Flex wrap="wrap" gap={2} alignItems="center" mb="4">
          <ColumnVisibility table={table} />
          <Filters
            columnFilters={columnFilters}
            setColumnFilters={onColumnFiltersChange}
            table={table}
            filterExtraInfo={filterExtraInfo}
          />
          <GlobalFilter
            globalFilter={globalFilter}
            setGlobalFilter={onGlobalFilterChange}
            maxWidth={{ base: 'none', md: '250px' }}
            flex={{ base: 1, md: 'initial' }}
            minW="150px"
          />
          <ActiveFilters
            columnFilters={columnFilters}
            removeItemFunc={(elem) => {
              onColumnFiltersChange(columnFilters.filter((f) => f.id !== elem.id));
            }}
            filterExtraInfo={filterExtraInfo}
          />
          <Pagination
            table={table}
            pageInfo={data.listClients.pageInfo}
            totalRows={totalCount}
            tablePrefix={tablePrefix}
            ml="auto"
          />
        </Flex>

        <TanstackTableView table={table} />
      </>
    </Box>
  );
}
